import apiClient from "./apiClient";
import { API_BASE_URL, ApiResponse } from "./config";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type ApplicationStatus =
  | "DRAFT"
  | "PENDING_REVIEW"
  | "APPROVED"
  | "SUBMITTED"
  | "UNDER_REVIEW"
  | "ACCEPTED"
  | "REJECTED"
  | "WAITLISTED"
  | "WITHDRAWN";

export type PaymentStatus =
  | "PENDING"
  | "PROCESSING"
  | "COMPLETED"
  | "FAILED"
  | "REFUNDED";

export type Gender = "MALE" | "FEMALE" | "OTHER" | "PREFER_NOT_TO_SAY";

export type DocumentType =
  | "MATRIC_MOFA_ATTESTED"
  | "INTER_MOFA_ATTESTED"
  | "MATRIC_MARKSHEET_MOFA"
  | "INTER_MARKSHEET_MOFA"
  | "BACHELORS_TRANSCRIPT_HEC_MOFA"
  | "MASTERS_TRANSCRIPT_HEC_MOFA"
  | "FAMILY_REGISTRATION_CERTIFICATE"
  | "PARENTS_CNIC_ENGLISH"
  | "APPLICANT_CNIC"
  | "IELTS_CERTIFICATE"
  | "TOEFL_CERTIFICATE"
  | "PASSPORT_PHOTO"
  | "PASSPORT"
  | "TRANSCRIPT"
  | "DIPLOMA"
  | "LANGUAGE_CERTIFICATE"
  | "MOTIVATION_LETTER"
  | "RECOMMENDATION_LETTER"
  | "CV"
  | "PORTFOLIO"
  | "OTHER";

export interface Bank {
  bank_code: string;
  name: string;
  recurring_allowed: boolean;
  otp_required: boolean;
}

export interface RequiredDocument {
  type: DocumentType;
  description: string;
  required: boolean;
}

export interface RequiredDocumentsResponse {
  courseLevel: string;
  requiredDocuments: RequiredDocument[];
}

export interface CreateApplicationRequest {
  courseId: string;
  givenNames: string;
  familyName: string;
  gender: Gender;
  dateOfBirth: string; // ISO date string
  nationality: string;
  placeOfBirth: string;
  email: string;
  whatsappNumber: string;
  passportNumber: string;
  passportExpiry: string; // ISO date string
}

export interface UpdateApplicationRequest {
  personalStatement?: string;
  academicHistory?: string;
  workExperience?: string;
  references?: string;
}

export interface Application {
  id: string;
  applicationNumber: string;
  status: ApplicationStatus;
  givenNames: string | null;
  familyName: string | null;
  gender: Gender | null;
  dateOfBirth: string | null;
  nationality: string | null;
  placeOfBirth: string | null;
  email: string | null;
  whatsappNumber: string | null;
  passportNumber: string | null;
  passportExpiry: string | null;
  photoUrl: string | null;
  personalStatement: string | null;
  academicHistory: string | null;
  workExperience: string | null;
  references: string | null;
  paymentStatus: PaymentStatus;
  paymentIntentId: string | null;
  paymentReference: string | null;
  paidAt: string | null;
  submittedAt: string | null;
  createdAt: string;
  updatedAt: string;
  course: {
    id: string;
    title: string;
    level: string;
    university: {
      id: string;
      name: string;
      city: string;
      country: string;
    };
  };
  intake: {
    id: string;
    name: string;
    startDate: string;
    applicationDeadline: string;
  } | null;
  documents: Document[];
}

export interface Document {
  id: string;
  name: string;
  originalName: string;
  documentType: DocumentType;
  size: number;
  mimeType: string;
  isVerified: boolean;
  uploadedAt: string;
  url?: string;
}

export interface ApplicationListItem {
  id: string;
  applicationNumber: string;
  status: ApplicationStatus;
  givenNames: string | null;
  familyName: string | null;
  createdAt: string;
  courseId: string;
  course: {
    id: string;
    title: string;
    level: string;
  };
  intake: {
    name: string;
  } | null;
}

export interface ApplicationsResponse {
  applications: ApplicationListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface DocumentsResponse {
  applicationId: string;
  totalDocuments: number;
  documents: Document[];
  documentsByType: Record<DocumentType, Document[]>;
}

export interface PaymentIntentResponse {
  paymentIntent: {
    id: string;
    redirectUrl: string;
    amount: number;
    currency: string;
    status: string;
  };
  application: {
    id: string;
    applicationNumber: string;
    course: any;
    amount: number;
  };
}

export interface ValidationResponse {
  canProceedToPayment: boolean;
  missingDocuments?: string[];
}

// ============================================================================
// APPLICATION SERVICE
// ============================================================================

class ApplicationService {
  /**
   * Get required documents for a course
   */
  async getRequiredDocuments(
    courseId: string
  ): Promise<RequiredDocumentsResponse> {
    const response = await apiClient.get<{ data: RequiredDocumentsResponse }>(
      `/api/applications/required-documents/${courseId}`,
      undefined,
      true
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || "Failed to fetch required documents");
    }

    // Backend wraps response in { success, message, data }
    return (response.data as any).data || response.data;
  }

  /**
   * Create a new application
   */
  async createApplication(
    data: CreateApplicationRequest
  ): Promise<Application> {
    const response = await apiClient.post<{ data: Application }>(
      "/api/applications",
      data,
      true
    );

    if (!response.success || !response.data) {
      const details = response.error?.details as any;
      const err = new Error(response.error?.message || "Failed to create application") as any;
      if (details?.errors?.length) {
        err.validationErrors = details.errors;
      }
      throw err;
    }

    return (response.data as any).data || response.data;
  }

  /**
   * Get user's applications
   */
  async getApplications(params?: {
    page?: number;
    limit?: number;
  }): Promise<ApplicationsResponse> {
    const response = await apiClient.get<{ data: ApplicationsResponse }>(
      "/api/applications",
      params,
      true
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || "Failed to fetch applications");
    }

    // Backend wraps response in { success, message, data }
    // apiClient already extracts the outer data, so we need to get data.data
    return (response.data as any).data || response.data;
  }

  /**
   * Get application details
   */
  async getApplicationById(id: string): Promise<Application> {
    const response = await apiClient.get<{ data: Application }>(
      `/api/applications/${id}`,
      undefined,
      true
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || "Failed to fetch application details");
    }

    return (response.data as any).data || response.data;
  }

  /**
   * Update application content
   */
  async updateApplication(
    id: string,
    data: UpdateApplicationRequest
  ): Promise<Application> {
    const response = await apiClient.put<{ data: Application }>(
      `/api/applications/${id}`,
      data,
      true
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || "Failed to update application");
    }

    return (response.data as any).data || response.data;
  }

  /**
   * Delete application (only DRAFT status)
   */
  async deleteApplication(id: string): Promise<{ message: string }> {
    const response = await apiClient.delete<{ message: string }>(
      `/api/applications/${id}`,
      true
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || "Failed to delete application");
    }

    return response.data;
  }

  /**
   * Upload documents for an application
   */
  async uploadDocuments(
    applicationId: string,
    files: File[],
    documentTypes: DocumentType[]
  ): Promise<ApiResponse<Document[]>> {
    if (files.length !== documentTypes.length) {
      throw new Error("Number of files must match number of document types");
    }

    const formData = new FormData();

    // Append all files
    files.forEach((file) => {
      formData.append("documents", file);
    });

    // Append document types as JSON array
    formData.append("documentTypes", JSON.stringify(documentTypes));

    const token = localStorage.getItem("authToken");
    const headers: HeadersInit = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    try {
      const fullUrl = `${API_BASE_URL}/api/applications/${applicationId}/documents`;
      const response = await fetch(fullUrl, {
        method: "POST",
        headers,
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to upload documents");
      }

      return data;
    } catch (error) {
      console.error("Document upload error:", error);
      throw error;
    }
  }

  /**
   * Get uploaded documents for an application
   */
  async getDocuments(applicationId: string): Promise<DocumentsResponse> {
    const response = await apiClient.get<{ data: DocumentsResponse }>(
      `/api/applications/${applicationId}/documents`,
      undefined,
      true
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || "Failed to fetch documents");
    }

    return (response.data as any).data || response.data;
  }

  /**
   * Create payment intent
   */
  async createPaymentIntent(
    applicationId: string
  ): Promise<PaymentIntentResponse> {
    const response = await apiClient.post<PaymentIntentResponse>(
      `/api/applications/${applicationId}/payment`,
      {},
      true
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || "Failed to create payment intent");
    }

    return response.data;
  }

  /**
   * Validate application before submission
   */
  async validateApplication(
    applicationId: string
  ): Promise<ValidationResponse> {
    const response = await apiClient.post<{ data: ValidationResponse }>(
      `/api/applications/${applicationId}/validate`,
      {},
      true
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || "Validation failed");
    }

    return (response.data as any).data || response.data;
  }

  /**
   * Submit application for review
   */
  async submitApplication(
    applicationId: string
  ): Promise<Application> {
    const response = await apiClient.post<{ data: Application }>(
      `/api/applications/${applicationId}/submit`,
      {},
      true
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || "Failed to submit application");
    }

    return (response.data as any).data || response.data;
  }

  /**
   * Delete a specific document
   */
  async deleteDocument(
    applicationId: string,
    documentId: string
  ): Promise<void> {
    const response = await apiClient.delete<null>(
      `/api/applications/${applicationId}/documents/${documentId}`,
      true
    );

    if (!response.success) {
      throw new Error(response.error?.message || "Failed to delete document");
    }
  }

  /**
   * GoPayFast: Validate payment and send OTP
   */
  async validatePayment(data: {
    applicationId: string;
    accountTypeId: string;
    cardNumber?: string;
    expiryMonth?: string;
    expiryYear?: string;
    cvv?: string;
    bankCode?: string;
    accountNumber?: string;
    cnicNumber?: string;
    accountTitle?: string;
  }): Promise<ApiResponse<any>> {
    const response = await apiClient.post<any>(
      `/api/gopayfast/validate`,
      data,
      true
    );

    if (!response.success) {
      throw new Error(response.error?.message || "Failed to validate payment");
    }

    return response;
  }

  /**
   * GoPayFast: Complete transaction with OTP
   */
  async completePayment(data: {
    applicationId: string;
    transactionId: string;
    otp: string;
    accountTypeId: string;
    cardNumber?: string;
    expiryMonth?: string;
    expiryYear?: string;
    cvv?: string;
    bankCode?: string;
    accountNumber?: string;
    cnicNumber?: string;
    accountTitle?: string;
  }): Promise<ApiResponse<any>> {
    const response = await apiClient.post<any>(
      `/api/gopayfast/transaction`,
      data,
      true
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || "Payment failed");
    }

    return response;
  }

  /**
   * Check if user has an existing draft application for a course
   */
  async hasExistingDraftApplication(courseId: string): Promise<string | null> {
    try {
      const response = await this.getApplications({ page: 1, limit: 100 });
      const draftApp = response.applications.find(
        (app: ApplicationListItem) => app.course.title && app.status === "DRAFT"
      );
      return draftApp?.id || null;
    } catch (error) {
      console.error("Error checking for existing draft:", error);
      return null;
    }
  }

  /**
   * GoPayFast: Get list of available banks
   */
  async getBankList(): Promise<Bank[]> {
    const response = await apiClient.get<{ data: Bank[] }>(
      `/api/gopayfast/banks`,
      undefined,
      true
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || "Failed to fetch bank list");
    }

    return (response.data as any).data || response.data;
  }

  /**
   * PayFast: Create payment request (redirect-based)
   */
  async createPayFastPayment(applicationId: string): Promise<{
    paymentData: any;
    transactionUrl: string;
  }> {
    const response = await apiClient.post<any>(
      `/api/gopayfast/create-payment`,
      { applicationId },
      true
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || "Failed to create payment");
    }

    // Backend returns data nested in response.data.data
    return (response.data as any).data || response.data;
  }

  /**
   * PayFast: Get payment status
   */
  async getPaymentStatus(applicationId: string): Promise<any> {
    const response = await apiClient.get<any>(
      `/api/gopayfast/status/${applicationId}`,
      undefined,
      true
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || "Failed to get payment status");
    }

    return response.data;
  }
}

const applicationService = new ApplicationService();
export default applicationService;
